import React, { useCallback, useState } from 'react';
import Cropper from 'react-easy-crop';
import { ZoomIn, ZoomOut, RotateCcw, Check, X } from 'lucide-react';

/**
 * Modal that lets the admin crop + zoom + rotate an image before uploading.
 * Returns a `Blob` (JPEG, 92% quality) via `onConfirm(blob)` once the user
 * accepts the crop. `aspect` defaults to 1 (square) — interpreter photos look
 * best in a circle crop.
 */
export default function ImageCropperModal({ srcDataUrl, aspect = 1, shape = 'round', onConfirm, onCancel }) {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [pixelCrop, setPixelCrop] = useState(null);
  const [busy, setBusy] = useState(false);

  const onCropComplete = useCallback((_, area) => setPixelCrop(area), []);

  async function handleConfirm() {
    if (!pixelCrop) return;
    setBusy(true);
    try {
      const blob = await getCroppedBlob(srcDataUrl, pixelCrop, rotation);
      await onConfirm(blob);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/80" data-testid="image-cropper-modal">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
          <h3 className="font-semibold text-charcoal-800">Ajustar foto</h3>
          <button onClick={onCancel} className="p-1 text-charcoal-500 hover:text-charcoal-800" data-testid="cropper-cancel-btn">
            <X size={18} />
          </button>
        </div>

        <div className="relative w-full h-[420px] bg-gray-900">
          <Cropper
            image={srcDataUrl}
            crop={crop}
            zoom={zoom}
            rotation={rotation}
            aspect={aspect}
            cropShape={shape}
            minZoom={0.5}
            maxZoom={3}
            restrictPosition={false}
            showGrid
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={onCropComplete}
          />
        </div>

        <div className="px-5 py-4 border-t border-gray-200 space-y-3">
          <div className="flex items-center gap-3">
            <ZoomOut size={16} className="text-charcoal-500 flex-shrink-0" />
            <input
              type="range"
              min={0.5}
              max={3}
              step={0.05}
              value={zoom}
              onChange={(e) => setZoom(Number(e.target.value))}
              data-testid="cropper-zoom-slider"
              className="flex-1 accent-cmic-600"
            />
            <ZoomIn size={16} className="text-charcoal-500 flex-shrink-0" />
            <span className="text-xs text-charcoal-500 w-12 text-right tabular-nums">{Math.round(zoom * 100)}%</span>
          </div>
          <div className="flex items-center justify-between">
            <button
              type="button"
              onClick={() => setRotation((rotation + 90) % 360)}
              data-testid="cropper-rotate-btn"
              className="inline-flex items-center gap-1.5 text-xs text-charcoal-600 hover:text-cmic-700 px-3 py-1.5 rounded-lg hover:bg-gray-50"
            >
              <RotateCcw size={13} />Rotar 90°
            </button>
            <p className="text-[11px] text-charcoal-400">
              Arrastra para mover · usa el slider para hacer zoom
            </p>
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-gray-200 bg-gray-50 rounded-b-2xl">
          <button
            onClick={onCancel}
            disabled={busy}
            className="px-4 py-2 text-sm font-medium text-charcoal-600 hover:text-charcoal-800 disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            onClick={handleConfirm}
            disabled={busy || !pixelCrop}
            data-testid="cropper-confirm-btn"
            className="inline-flex items-center gap-2 bg-cmic-600 text-white rounded-lg px-4 py-2 text-sm font-medium hover:bg-cmic-700 disabled:opacity-50"
          >
            <Check size={14} />{busy ? 'Procesando…' : 'Guardar recorte'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ---------- helpers ---------- */
function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

/** Returns a JPEG Blob with the cropped + rotated region. */
async function getCroppedBlob(srcDataUrl, pixelCrop, rotation) {
  const image = await loadImage(srcDataUrl);
  const rad = (rotation * Math.PI) / 180;
  const sin = Math.abs(Math.sin(rad));
  const cos = Math.abs(Math.cos(rad));

  // Step 1: Draw the rotated image to a temporary canvas
  const rotCanvas = document.createElement('canvas');
  rotCanvas.width = image.width * cos + image.height * sin;
  rotCanvas.height = image.width * sin + image.height * cos;
  const rotCtx = rotCanvas.getContext('2d');
  rotCtx.translate(rotCanvas.width / 2, rotCanvas.height / 2);
  rotCtx.rotate(rad);
  rotCtx.drawImage(image, -image.width / 2, -image.height / 2);

  // Step 2: Crop from the rotated canvas
  const out = document.createElement('canvas');
  out.width = pixelCrop.width;
  out.height = pixelCrop.height;
  const outCtx = out.getContext('2d');
  // Fill white so zoomed-out areas (outside the image bounds) export as white,
  // not black, in the JPEG.
  outCtx.fillStyle = '#ffffff';
  outCtx.fillRect(0, 0, out.width, out.height);
  outCtx.drawImage(
    rotCanvas,
    pixelCrop.x, pixelCrop.y, pixelCrop.width, pixelCrop.height,
    0, 0, pixelCrop.width, pixelCrop.height,
  );

  return new Promise((resolve) => {
    out.toBlob((blob) => resolve(blob), 'image/jpeg', 0.92);
  });
}
